import React from 'react';
import { Box, Text } from '@wix/design-system';
import {
  useCurrentModule,
  DEFAULT_PURCHASE_CYCLE,
} from '@wix/package-picker-shared-eclipse';
import {
  usePrice,
  DisplayPriceData,
} from '@wix/premium-purchase-platform/@components';
import s from './EclipsePrice.scss';

const FULL_PRICE_HEIGHT = '24px';
// CLDR currency placeholder (U+00A4) used in currencySettings patterns
const CURRENCY_PLACEHOLDER = '¤';

interface EclipsePriceProps {
  productId: string;
  /**
   * Where the struck-through catalog price sits relative to the price.
   * Defaults to `below`, as the plan cards render it. The Quick Pay lightbox
   * uses `inline`, where the design places the two side by side.
   */
  strikethroughPosition?: 'below' | 'inline';
}

export const EclipsePrice = ({
  productId,
  strikethroughPosition = 'below',
}: EclipsePriceProps) => {
  const module = useCurrentModule();
  const { t } = module.internals.useTranslations();

  const priceData = usePrice({
    productId,
    cycle: DEFAULT_PURCHASE_CYCLE,
  }) as DisplayPriceData;
  const { data: offering } =
    module.SDK.eclipseOfferingApiClient.hooks.useFetchOffering({});

  const { data: product } =
    module.SDK.eclipseOfferingApiClient.hooks.useFetchProduct({ productId });
  const { data: discountData } =
    module.SDK.eclipseOfferingApiClient.hooks.useFetchOfferingDiscount({});

  const { hasSale } = product ?? {};
  const hasGeneralSale = discountData?.hasSale;

  const { value, fullPrice, showCatalogPrice, taxLabel, showTaxLabel } =
    priceData;

  const { currencySettings } = offering ?? {};
  const { decimalSeparator = '.', positiveSuffix } = currencySettings ?? {};

  // If the locale's suffix pattern contains the currency placeholder, render currency as a suffix.
  // Otherwise default to prefix (covers both prefix locales and the fallback when currencySettings is missing).
  const currencyPosition: 'prefix' | 'suffix' = positiveSuffix?.includes(
    CURRENCY_PLACEHOLDER,
  )
    ? 'suffix'
    : 'prefix';

  const fullPriceSlot =
    !hasSale && hasGeneralSale
      ? () => <Box height={FULL_PRICE_HEIGHT} />
      : undefined;

  const strikethroughContent = fullPriceSlot ? (
    fullPriceSlot()
  ) : showCatalogPrice && fullPrice ? (
    <span className={s.strikethrough}>{fullPrice}</span>
  ) : null;

  const isInline = strikethroughPosition === 'inline';

  return (
    <Box
      className={s.priceWrapper}
      direction="vertical"
      gap={isInline ? '4px' : undefined}
    >
      <Box
        className={`${s.priceRoot} ${isInline ? s.priceRootInline : ''}`}
        direction={isInline ? 'horizontal' : 'vertical'}
        gap={isInline ? '12px' : undefined}
        verticalAlign={isInline ? 'bottom' : undefined}
      >
        <Box className={s.priceLine}>
          {currencyPosition === 'prefix' && (
            <span className={s.currency}>{value.currency}</span>
          )}
          <span className={s.integer}>{value.integer}</span>
          <Box
            className={`${s.fractionColumn} ${
              !value.fraction ? s.fractionColumnNoFraction : ''
            }`}
          >
            {value.fraction && (
              <span className={s.fraction}>
                {decimalSeparator}
                {value.fraction}
              </span>
            )}
            {currencyPosition === 'suffix' && (
              <span className={`${s.currency} ${s.currencySuffix}`}>
                {value.currency}
              </span>
            )}
            <span
              className={s.cycleLabel}
            >{`/${t('product_price.month')}`}</span>
          </Box>
        </Box>

        {strikethroughContent}
      </Box>

      {/* The tax note ("Price includes VAT" in inclusive-tax GEOs) sits on its
          own line below the price — inline beside it, it crowded the price and
          wrapped badly in those GEOs. */}
      {showTaxLabel && taxLabel && (
        <Text size="small" secondary>
          {taxLabel}
        </Text>
      )}
    </Box>
  );
};
